import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import {
  BRAND_FACTS_NAVIGATOR,
  type BrandFactsMove,
  resumeBrandFactsScreen,
} from '@/lib/setup/brand-facts-navigation'
import { screenIndex } from '@/lib/setup/setup-screen'
import { CONFIRM_STAMP_WAIT_MS, whenChanged } from '@/lib/setup/when-changed'
import {
  type StartBrandFactsInput,
  confirmBrandFacts as repoConfirmBrandFacts,
  subscribeLatestBrandFactsRun as repoSubscribeLatestBrandFactsRun,
} from '@/repositories/brand-facts'
import { isOnboardingLaterStepRunningReason } from '@/repositories/setup'
import { type StartBrandFactsOutcome, useBrandStore } from '@/stores/brand'
import { useSetupPositionStore } from '@/stores/setup-position'
import { currentSetupScope, useLatestRun } from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import { type BrandFactsRun, isBrandFactsApproved } from '@/types/brand-facts'
import { isError, isLoaded } from '@/types/load-state'
import type { StepConfirmation } from '@/types/onboarding-chain'
import { isRunFailed, runActivity } from '@/types/setup-run'

/** Step one of setup: the latest read, the confirm, and the screens between them; the sheet is the brand store's `brandFacts`. */
export const useBrandFactsSetupStore = defineStore('setup-brand-facts', () => {
  const brand = useBrandStore()
  const runSubscription = useLatestRun<BrandFactsRun>(repoSubscribeLatestBrandFactsRun)

  const facts = computed(() => brand.brandFacts)
  const latestRun = runSubscription.state
  const run = runSubscription.run

  const awaitedRunId = ref<string | null>(null)
  const confirming = ref(false)
  const followedRunId = ref<string | null>(null)

  function open(): void {
    const scope = currentSetupScope()
    if (!scope) {
      runSubscription.fail(i18n.global.t('common.noWorkspaceSelected'))
      return
    }
    runSubscription.open(scope)
  }

  function close(): void {
    runSubscription.close()
    awaitedRunId.value = null
    followedRunId.value = null
  }

  const resolved = computed(
    () => facts.value.status !== 'loading' && latestRun.value.status !== 'loading',
  )

  const readFailed = computed(() => isError(facts.value) || isError(latestRun.value))

  const finished = computed(() => isLoaded(facts.value) && isBrandFactsApproved(facts.value.value))

  const confirmation = computed<StepConfirmation>(() => ({
    confirmed: finished.value,
    confirmedAt: isLoaded(facts.value) ? facts.value.value.meta.confirmedAt : null,
  }))

  watch(run, (latest) => {
    if (latest !== null && latest.id === awaitedRunId.value) awaitedRunId.value = null
  })

  const starting = computed(() => brand.startingBrandFactsRead || awaitedRunId.value !== null)

  const isReading = runSubscription.isLive

  const activity = computed(() => runActivity(run.value))

  function resumeIndex(): number {
    const sheet = isLoaded(facts.value) ? facts.value.value : null
    return screenIndex(
      BRAND_FACTS_NAVIGATOR.screens,
      resumeBrandFactsScreen({
        hasSheet: sheet !== null,
        runLive: isReading.value,
        runFailed: run.value !== null && isRunFailed(run.value),
      }),
    )
  }

  function move(applied: BrandFactsMove): void {
    useSetupPositionStore().move('brand-facts', BRAND_FACTS_NAVIGATOR, applied)
  }

  function followRun(runId: string): void {
    followedRunId.value = runId
  }

  watch([run, followedRunId], ([latest, followed]) => {
    if (followed === null || latest?.id !== followed) return
    followedRunId.value = null
    move('read-opened')
  })

  async function start(links: StartBrandFactsInput): Promise<StartBrandFactsOutcome> {
    const outcome = await brand.startBrandFactsRead(links)
    if (outcome.status === 'started' && run.value?.id !== outcome.runId) {
      awaitedRunId.value = outcome.runId
    }
    return outcome
  }

  async function confirm(): Promise<boolean> {
    confirming.value = true
    try {
      const before = confirmation.value.confirmedAt
      await repoConfirmBrandFacts()
      await whenChanged(() => confirmation.value.confirmedAt, before, CONFIRM_STAMP_WAIT_MS)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (isOnboardingLaterStepRunningReason(parsed.reason)) {
        useToastStore().error(i18n.global.t('setup.locked.laterStepRunning'))
      } else {
        useToastStore().error(i18n.global.t('setup.errors.confirmFailed'), parsed.message)
      }
      return false
    } finally {
      confirming.value = false
    }
  }

  return {
    latestRun,
    run,
    isReading,
    starting,
    confirming,
    resolved,
    readFailed,
    finished,
    confirmation,
    activity,
    open,
    close,
    resumeIndex,
    move,
    followRun,
    start,
    confirm,
  }
})
